import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, Button, Input, Form, message, Alert, Spin, theme } from 'antd';
import { QrcodeOutlined, SafetyCertificateOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { apiService } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';

const { Title, Text } = Typography;

interface MFASetupData {
  secret: string;
  qrCode: string;
  manualEntryKey: string;
}

export const MFASetup: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(true);
  const [mfaData, setMfaData] = useState<MFASetupData | null>(null);
  const [step, setStep] = useState<'setup' | 'verify'>('setup');
  const { logout, tempToken } = useAuth();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { isDarkMode } = useTheme();

  const initializeMFASetup = useCallback(async () => {
    try {
      const data = await apiService.setupMFA(tempToken || undefined);
      setMfaData(data);
    } catch (error) {
      message.error('Failed to initialize MFA setup');
      console.error('MFA setup error:', error);
    } finally {
      setSetupLoading(false);
    }
  }, [tempToken]);

  useEffect(() => {
    initializeMFASetup();
  }, [initializeMFASetup]);

  const onVerifyFinish = async (values: { token: string }) => {
    setLoading(true);
    try {
      const response = await apiService.enableMFA(values.token, tempToken || undefined);
      message.success('MFA enabled successfully!');
      // Use the JWT token from the response
      apiService.setToken(response.jwt);
      localStorage.setItem('auth_token', response.jwt);
      navigate('/projects');
    } catch (error) {
      message.error('Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    logout();
  };

  if (setupLoading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Card style={{ width: 500, textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            <Text>Setting up MFA...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Card style={{ width: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <SafetyCertificateOutlined style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }} />
            <Title level={3}>Verify MFA Setup</Title>
            <Text type="secondary">
              Enter the 6-digit code from your authenticator app to complete setup
            </Text>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onVerifyFinish}
          >
            <Form.Item
              name="token"
              label="Verification Code"
              rules={[
                { required: true, message: 'Please enter the verification code' },
                { len: 6, message: 'Code must be 6 digits' },
                { pattern: /^\d{6}$/, message: 'Code must contain only numbers' }
              ]}
            >
              <Input
                placeholder="123456"
                maxLength={6}
                style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '2px' }}
              />
            </Form.Item>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading} 
                size="large"
                block
              >
                Verify & Enable MFA
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Button 
              type="link" 
              onClick={() => setStep('setup')}
            >
              Back to Setup Instructions
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <Card style={{ width: 500, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <QrcodeOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
          <Title level={3}>Setup Multi-Factor Authentication</Title>
          <Text type="secondary">
            Secure your account with an authenticator app
          </Text>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img 
            src={mfaData?.qrCode} 
            alt="MFA QR Code" 
            style={{ 
              border: '1px solid #d9d9d9', 
              borderRadius: '8px',
              maxWidth: '200px'
            }} 
          />
        </div>

        <Alert
          message="Setup Instructions"
          description={
            <div>
              <p><strong>Step 1:</strong> Install an authenticator app on your phone:</p>
              <ul style={{ textAlign: 'left', margin: '8px 0' }}>
                <li>Google Authenticator</li>
                <li>Microsoft Authenticator</li>
                <li>Authy</li>
                <li>1Password</li>
              </ul>
              <p><strong>Step 2:</strong> Scan the QR code above with your authenticator app</p>
              <p><strong>Step 3:</strong> Click "Verify Setup" and enter the 6-digit code</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <div style={{ marginBottom: '24px' }}>
          <Text strong>Manual Entry Key:</Text>
          <div style={{ 
            background: isDarkMode ? token.colorBgElevated : token.colorBgLayout, 
            padding: '8px', 
            borderRadius: '4px', 
            fontFamily: 'monospace',
            wordBreak: 'break-all',
            marginTop: '4px',
            border: `1px solid ${token.colorBorder}`,
            color: token.colorText
          }}>
            {mfaData?.manualEntryKey}
          </div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Use this key if you can't scan the QR code
          </Text>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <Button 
            type="primary" 
            onClick={() => setStep('verify')}
            size="large"
            style={{ flex: 1 }}
          >
            Verify Setup
          </Button>
          <Button 
            onClick={handleBackToLogin}
            size="large"
            icon={<ArrowLeftOutlined />}
          >
            Back to Login
          </Button>
        </div>
      </Card>
    </div>
  );
};
